<!-- ══════════════════════════════════════════════════════════════════════════
     ALTA Y EDICIÓN DE UN COMERCIO — página, no modal.

     🔴 REDISEÑO 2026-09-20 (contrato del rediseño): *«demasiada caja, demasiado
     contenido, la vista se pierde»*, *«no quiero esas cajas grandes»*.

     Lo que salió, y por qué (medido antes de tocar nada):
       · las CUATRO tarjetas — Identificación, Cuenta, Adjuntos y Contactos eran
         cuatro cajas con borde y sombra alrededor de campos de formulario. Una
         caja se gana su existencia cuando se toca y lleva a otro lado; agrupar
         campos no lo es. Pasaron a `.kb-seccion`: rótulo chico y una línea.
         Quedan en caja los dos bloques que SÍ se tocan y guardan algo aparte —
         el alta de un contacto y la fila de cada adjunto.
       · los CINCO subtítulos — el de la página y uno por tarjeta. Lo único que
         decían y no estaba en otro lado bajó al campo que describe, como
         renglón de ayuda.
       · el aviso azul de los adjuntos pasó a ser ese renglón: no era una
         alerta, era una explicación, y dos avisos apilados dejan de advertir.

     Queda UNA cinta, y sólo en el alta: de qué sirve el primer contacto. Es lo
     que se pasa por alto y deja al comercio sin poder entrar al portal.
     ══════════════════════════════════════════════════════════════════════════ -->
<div class="kb-page kb-gateway">

  <!-- ══ Migas ════════════════════════════════════════════════════════════════ -->
  <nav class="kb-migas" aria-label="Ubicación">
    <a class="kb-miga !text-muted-foreground hover:!text-foreground" [routerLink]="['/console/merchants']">Comercios</a>
    <span class="kb-miga-sep" aria-hidden="true"><i class="fas fa-chevron-right"></i></span>
    <ng-container *ngIf="editando">
      <a class="kb-miga !text-muted-foreground hover:!text-foreground" [routerLink]="['/console/merchants', codigo]">{{ formData.name || codigo }}</a>
      <span class="kb-miga-sep" aria-hidden="true"><i class="fas fa-chevron-right"></i></span>
    </ng-container>
    <span class="kb-miga-actual">{{ editando ? 'Editar' : 'Nuevo comercio' }}</span>
  </nav>

  <!-- El esqueleto conserva la FORMA de lo que viene: dos bloques de campos. -->
  <div *ngIf="loading" class="space-y-8" aria-live="polite" aria-busy="true">
    <div class="kb-skeleton h-9 w-1/3"></div>
    <div class="space-y-4">
      <div class="kb-skeleton h-3 w-28"></div>
      <div class="kb-skeleton h-10 w-full max-w-xl" *ngFor="let s of [1,2,3]"></div>
    </div>
  </div>

  <div class="kb-empty" *ngIf="noEncontrado">
    <span class="kb-empty-icono"><i class="fas fa-store-slash" aria-hidden="true"></i></span>
    <p class="kb-empty-titulo">No encontramos el comercio «{{ codigo }}»</p>
    <button type="button" class="kb-btn kb-btn-outline mt-4" (click)="volver()">Volver al listado</button>
  </div>

  <ng-container *ngIf="!loading && !noEncontrado">

    <!-- ══ Encabezado ═════════════════════════════════════════════════════════ -->
    <div class="kb-ficha">
      <div class="min-w-0">
        <h1 class="kb-ficha-titulo !font-bold">{{ editando ? 'Editar comercio' : 'Nuevo comercio' }}</h1>
        <!-- Quién es esto en datos. El renglón que explicaba qué se cambia y qué
             se versiona aparte se fue: es doctrina, no un dato de este comercio. -->
        <p class="kb-ficha-meta" *ngIf="editando">
          <span class="truncate">{{ formData.name || 'Sin nombre' }}</span>
          <span aria-hidden="true">·</span>
          <span class="font-mono">{{ codigo }}</span>
        </p>
      </div>
    </div>

    <!-- 🔴 2026-09-24, aprobado por Juan: la cinta azul «El primer contacto va a ser el primer
         usuario» se fue. Su frase vive ahora en la descripción de Contactos, que es donde aplica
         —y donde antes se decía lo contrario: «se pueden agregar después», cuando el alta NO deja
         crear sin uno—. -->

    <!-- 🔴 DOS COLUMNAS (2026-09-24, aprobado por Juan; regla de la casa: una pantalla de trabajo
         cabe sin scroll). A la izquierda el comercio; a la derecha con quién se habla, de donde
         sale el usuario del portal. En pantallas angostas se apilan. -->
    <div class="grid items-start gap-x-[32px] gap-y-[32px] xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
     <div class="min-w-0 space-y-8">

      <!-- ══ Identificación ═══════════════════════════════════════════════════ -->
      <section class="kb-tarjeta">
        <div class="kb-tarjeta-cab">
          <h2 class="text-base font-semibold leading-none">Identificación</h2>
        </div>
        <div class="kb-tarjeta-cuerpo">

        <div class="space-y-5">
          <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label class="kb-label" for="cmCodigo">
                Código <span class="text-destructive">*</span>
                <i class="fas fa-lock kb-icono-fila" *ngIf="editando" aria-hidden="true"></i>
              </label>
              <input id="cmCodigo" type="text" class="kb-input font-mono uppercase placeholder:normal-case placeholder:font-sans" maxlength="12" placeholder="p. ej. VM"
                     [(ngModel)]="formData.code" [disabled]="editando">
              <p class="kb-hint">
                {{ editando
                   ? 'El código no se puede modificar: lo referencian los datos ya emitidos.'
                   : 'Corto, es lo que se cita en los reportes y las liquidaciones.' }}
              </p>
            </div>
            <div>
              <label class="kb-label" for="cmRif">RIF <span class="text-destructive">*</span></label>
              <input id="cmRif" type="text" class="kb-input font-mono tabular-nums" placeholder="J-00000000-0"
                     [(ngModel)]="formData.tax_id" (blur)="normalizarRif('tax_id')">
              <p class="kb-hint">Se ordena solo al salir del campo. Formato J-00000000-0.</p>
            </div>
          </div>

          <div>
            <label class="kb-label" for="cmNombre">Nombre <span class="text-destructive">*</span></label>
            <input id="cmNombre" type="text" class="kb-input" placeholder="p. ej. VMagic" [(ngModel)]="formData.name">
            <p class="kb-hint">El nombre comercial, como se lo conoce. Es con el que figura en los documentos.</p>
          </div>

          <div>
            <label class="kb-label" for="cmRazon">Razón social</label>
            <input id="cmRazon" type="text" class="kb-input" placeholder="Como figura en el registro mercantil"
                   [(ngModel)]="formData.legal_name">
          </div>

          <div *ngFor="let p of PIEZAS_FISCAL">
            <label class="kb-label" [for]="'cmFiscal_' + p.clave">{{ p.rotulo }} <span class="text-destructive" *ngIf="p.obligatorio">*</span></label>
            <input [id]="'cmFiscal_' + p.clave" type="text" class="kb-input" [(ngModel)]="formData.fiscal[p.clave]">
          </div>

          <!-- 📍 Teléfono, correo y web DEL COMERCIO. Estaban en el modelo y el
               comercio los podía cargar desde su portal, pero desde esta
               pantalla no se podían llenar: el ejecutivo los tenía y no tenía
               dónde ponerlos. No son los del contacto —eso es una persona—
               sino los del negocio. -->
          <div class="grid grid-cols-1 gap-5 sm:grid-cols-3">
            <div>
              <label class="kb-label" for="cmTel">Teléfono</label>
              <input id="cmTel" type="tel" class="kb-input tabular-nums" placeholder="0212-0000000"
                     [(ngModel)]="formData.phone">
            </div>
            <div>
              <label class="kb-label" for="cmMail">Correo</label>
              <input id="cmMail" type="email" class="kb-input" placeholder="contacto@comercio.com"
                     [(ngModel)]="formData.email">
            </div>
            <div>
              <label class="kb-label" for="cmWeb">Sitio web</label>
              <input id="cmWeb" type="text" class="kb-input" placeholder="www.comercio.com"
                     [(ngModel)]="formData.website">
            </div>
          </div>
        </div>
        </div>
        <p class="kb-hint px-6" *ngIf="!editando">
          Los documentos (RIF, registro mercantil, cédula del representante) se suben después de
          crearlo, al editar la ficha.
        </p>
      </section>

      <!-- ══ Cuenta de liquidación — APAGADA ══════════════════════════════════
           Pedido del dueño del producto (2026-08-21). No se borra y el dato NO
           se pierde: si el comercio ya tiene cuenta cargada, se sigue leyendo y
           guardando igual. Volver a mostrarla es poner MOSTRAR_CUENTA en true.

           📍 Y hay un motivo de fondo: cambiar a dónde se le transfiere a un
           comercio es el vector de fraude más simple que existe. Merece su
           propio camino con confirmación, no un campo más en un formulario
           largo que se completa a las corridas. -->
      <section class="kb-tarjeta" *ngIf="MOSTRAR_CUENTA">
        <div class="kb-tarjeta-cab">
          <h2 class="text-base font-semibold leading-none">Cuenta de liquidación</h2>
        </div>
        <div class="kb-tarjeta-cuerpo">

        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label class="kb-label" for="cmCuenta">Número de cuenta</label>
            <input id="cmCuenta" type="text" class="kb-input font-mono tabular-nums"
                   placeholder="0000-0000-00-0000000000"
                   [(ngModel)]="formData.bank_account.account_number">
            <p class="kb-hint">
              No es obligatoria para crear el comercio, pero sin cuenta bancaria no se le
              puede liquidar. Tiene que estar antes del primer pago.
            </p>
          </div>
          <div>
            <label class="kb-label" for="cmTipoCuenta">Tipo de cuenta</label>
            <div class="kb-select-envoltorio">
              <select id="cmTipoCuenta" class="kb-select" [(ngModel)]="formData.bank_account.account_type">
                <option value="CORRIENTE">Corriente</option>
                <option value="AHORRO">Ahorro</option>
              </select>
              <i class="fas fa-chevron-down kb-select-flecha" aria-hidden="true"></i>
            </div>
          </div>
          <div>
            <label class="kb-label" for="cmTitular">Titular</label>
            <input id="cmTitular" type="text" class="kb-input" [(ngModel)]="formData.bank_account.holder">
          </div>
          <div>
            <label class="kb-label" for="cmRifTitular">RIF del titular</label>
            <input id="cmRifTitular" type="text" class="kb-input font-mono tabular-nums"
                   placeholder="J-00000000-0"
                   [(ngModel)]="formData.bank_account.tax_id" (blur)="normalizarRif('cuenta')">
          </div>
        </div>
        </div>
      </section>

      <!-- ══ Adjuntos ═════════════════════════════════════════════════════════
           🔴 Sólo al EDITAR: el endpoint es `/:code/documentos`, o sea que el
           comercio tiene que existir. En el alta se dice con todas las letras
           en vez de mostrar un botón que va a fallar. -->
      <section class="kb-tarjeta" *ngIf="editando">
        <div class="kb-tarjeta-cab">
          <h2 class="text-base font-semibold leading-none">Adjuntos</h2>
          <div class="kb-tarjeta-accion flex flex-wrap items-center gap-2">
          <span class="kb-badge shrink-0" *ngIf="editando"
                [ngClass]="faltaRif ? 'kb-badge-alerta' : 'kb-badge-exito'">
            {{ faltaRif ? 'Falta el RIF' : 'RIF cargado' }}
          </span>
          </div>
        </div>
        <div class="kb-tarjeta-cuerpo">

        <p class="kb-hint" *ngIf="!editando">
          Los adjuntos se cargan después de crear el comercio: guarda primero con el código,
          el nombre y el RIF, y al volver a esta pantalla vas a poder subirlos.
        </p>

        <div *ngIf="editando" class="divide-y divide-border">
          <div *ngFor="let t of TIPOS_ADJUNTO" class="flex flex-wrap items-center gap-3 py-3">
            <div class="min-w-[200px] flex-1">
              <p class="text-sm font-medium text-foreground">
                {{ t.label }}
                <span class="text-destructive" *ngIf="t.obligatorio">*</span>
              </p>
              <p class="kb-hint" *ngIf="documentosDe(t.kind).length === 0">Sin cargar</p>
              <p class="kb-td-sub" *ngFor="let d of documentosDe(t.kind)">
                <a [href]="urlArchivo(d.url)" target="_blank" rel="noopener"
                   class="text-primary hover:underline">{{ d.name }}</a>
                <span class="tabular-nums text-muted-foreground" *ngIf="d.size"> · {{ pesoLegible(d.size) }}</span>
                <button type="button" (click)="quitarAdjunto(d)"
                        class="ml-2 rounded p-0.5 text-muted-foreground transition-colors hover:bg-accent hover:text-destructive"
                        [attr.aria-label]="'Quitar el adjunto ' + d.name" title="Quitar este adjunto">
                  <i class="fas fa-times text-xs" aria-hidden="true"></i>
                </button>
              </p>
            </div>

            <!-- El `<input type=file>` va escondido y lo dispara la etiqueta:
                 el control nativo no se puede estilar y rompe la fila. -->
            <label class="kb-btn kb-btn-outline kb-btn-sm cursor-pointer">
              <i class="fas fa-upload" aria-hidden="true"></i>
              <span *ngIf="subiendo !== t.kind">
                {{ documentosDe(t.kind).length ? 'Agregar otro' : 'Subir' }}
              </span>
              <span *ngIf="subiendo === t.kind">Subiendo…</span>
              <input type="file" class="hidden" [disabled]="subiendo !== null"
                     [attr.aria-label]="'Subir ' + t.label"
                     (change)="subirAdjunto(t.kind, $event)"
                     accept=".pdf,.jpg,.jpeg,.png,.webp">
            </label>
          </div>
        </div>

        <p class="kb-hint" *ngIf="editando">
          El RIF es el único que hace falta; el resto suma, pero no frena nada. Se aceptan PDF
          y fotos: el RIF y la cédula hasta 8 MB, el registro mercantil hasta 15 MB.
        </p>
        </div>
      </section>

     </div>
      <!-- ══ Contactos ════════════════════════════════════════════════════════ -->
      <section class="kb-tarjeta">
        <div class="kb-tarjeta-cab">
          <h2 class="text-base font-semibold leading-none">Contactos</h2>
          <p class="m-0 text-sm text-muted-foreground" *ngIf="!editando">
            Hace falta al menos uno con nombre y correo: de él sale el usuario que entra al portal,
            con una clave inicial que el sistema le exige cambiar.
          </p>
          <p class="m-0 text-sm text-muted-foreground" *ngIf="editando">Con quién se habla en el comercio.</p>
          <div class="kb-tarjeta-accion col-start-2 row-span-2 row-start-1 flex flex-wrap items-center gap-2">
          <span class="shrink-0 text-sm tabular-nums text-muted-foreground"
                *ngIf="contactos.length">{{ contactos.length }}</span>
          </div>
        </div>
        <div class="kb-tarjeta-cuerpo">

        <!-- La caja se queda: es un bloque que se completa y se guarda aparte
             del resto del formulario. -->
        <div class="rounded-lg border border-border bg-muted/30 p-4">
          <p class="kb-section-title mb-3">
            {{ editandoContacto !== null ? 'Editando contacto' : 'Agregar contacto' }}
          </p>
          <!-- 2026-09-24: cada campo con su etiqueta ARRIBA. El nombre del campo iba de ejemplo
               adentro y desaparecía al escribir. -->
          <div class="grid grid-cols-1 gap-x-[16px] gap-y-[16px] sm:grid-cols-2">
            <div>
              <label class="kb-label" for="ctNombre">Nombre <span class="text-destructive">*</span></label>
              <input id="ctNombre" type="text" class="kb-input" [(ngModel)]="contactoActual.name">
            </div>
            <div>
              <label class="kb-label" for="ctApellido">Apellido</label>
              <input id="ctApellido" type="text" class="kb-input" [(ngModel)]="contactoActual.lastname">
            </div>
            <div class="sm:col-span-2">
              <label class="kb-label" for="ctCorreo">Correo <span class="text-destructive">*</span></label>
              <input id="ctCorreo" type="email" class="kb-input" placeholder="p. ej. nombre@comercio.com"
                     [(ngModel)]="contactoActual.email">
            </div>
            <div>
              <label class="kb-label" for="ctTelefono">Teléfono</label>
              <input id="ctTelefono" type="text" class="kb-input tabular-nums" [(ngModel)]="contactoActual.phone">
            </div>
            <div>
              <label class="kb-label" for="ctCargo">Cargo</label>
              <input id="ctCargo" type="text" class="kb-input" [(ngModel)]="contactoActual.position">
            </div>
          </div>
          <div class="mt-3 flex items-center gap-2">
            <button type="button" class="kb-btn kb-btn-outline kb-btn-sm" (click)="agregarContacto()">
              <i class="fas" [ngClass]="editandoContacto !== null ? 'fa-check' : 'fa-plus'" aria-hidden="true"></i>
              {{ editandoContacto !== null ? 'Guardar el contacto' : 'Agregar el contacto' }}
            </button>
            <button type="button" class="kb-btn kb-btn-ghost kb-btn-sm" *ngIf="editandoContacto !== null"
                    (click)="cancelarEdicionContacto()">Cancelar</button>
          </div>
          <p class="kb-hint">El nombre y el correo son obligatorios. No se admiten dos correos iguales.</p>
        </div>

        <div class="kb-tabla-plana" *ngIf="contactos.length">
          <table class="kb-table">
            <thead class="kb-thead">
              <tr>
                <th class="kb-th">Nombre</th>
                <th class="kb-th">Correo</th>
                <th class="kb-th kb-th-acciones">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr class="kb-tr last:border-b-0" *ngFor="let c of contactos; let i = index">
                <td class="kb-td">
                  <span class="kb-td-titulo">{{ c.name }} {{ c.lastname }}</span>
                  <p class="kb-td-sub" *ngIf="c.position">{{ c.position }}</p>
                </td>
                <td class="kb-td">
                  <span class="block">{{ c.email }}</span>
                  <span class="kb-td-sub block tabular-nums" *ngIf="c.phone">{{ c.phone }}</span>
                </td>
                <td class="kb-td-acciones">
                  <div class="kb-acciones-grupo">
                    <button type="button" class="kb-btn kb-btn-ghost kb-btn-icon" title="Editar el contacto"
                            aria-label="Editar el contacto"
                            (click)="editarContacto(i)"><i class="fas fa-edit" aria-hidden="true"></i></button>
                    <button type="button" class="kb-btn kb-btn-ghost kb-btn-icon text-destructive"
                            title="Quitar el contacto" aria-label="Quitar el contacto"
                            (click)="quitarContacto(i)">
                      <i class="fas fa-trash-alt" aria-hidden="true"></i></button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p class="kb-hint" *ngIf="!contactos.length">
          Todavía no hay contactos.
        </p>
        </div>
      </section>
    </div>

    <!-- ══ Barra de acciones, pegada al pie ═══════════════════════════════════ -->
    <div class="kb-barra-pie">
      <p class="kb-barra-nota">
        <!-- Dice QUÉ falta, no «faltan datos». Un aviso que enumera tres campos
             obliga a revisarlos de a uno; este nombra el que falta. -->
        <span class="kb-td-alerta font-medium" *ngIf="!valido">
          <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
          {{ faltaParaCrear || 'Faltan el código, el nombre o el RIF' }}
        </span>
        <!-- 2026-09-24: sólo si la cuenta se puede cargar AQUÍ; con la sección apagada (MOSTRAR_CUENTA)
             avisaba de algo que en esta pantalla no se puede resolver. La cuenta se carga después en
             la pestaña «Cuentas», y la ficha lo marca en «Alta del comercio». -->
        <span *ngIf="valido && !tieneCuenta && MOSTRAR_CUENTA">Sin cuenta bancaria todavía: no se le podrá liquidar</span>
        <span *ngIf="valido && !tieneCuenta && !MOSTRAR_CUENTA">Listo para crear</span>
        <span *ngIf="valido && tieneCuenta">Listo para guardar</span>
      </p>
      <div class="flex flex-wrap items-center justify-end gap-2">
        <button type="button" class="kb-btn kb-btn-outline" (click)="cancelar()">Cancelar</button>
        <button type="button" class="kb-btn kb-btn-primary" [disabled]="!valido || guardando" (click)="guardar()">
          <i class="fas" [ngClass]="guardando ? 'fa-spinner fa-spin' : 'fa-check'" aria-hidden="true"></i>
          {{ guardando ? 'Guardando…' : (editando ? 'Guardar los cambios' : 'Crear el comercio') }}
        </button>
      </div>
    </div>

  </ng-container>
</div>
